/**
 * AccountRequestListPage - Admin page for managing account requests
 *
 * Route: /admin/account-requests
 *
 * URL State:
 * - status: "all" | "pending" | "approved" | "rejected" (default: pending)
 * - search: string (default: empty)
 */

import { useState, useCallback, useMemo } from 'react'
import { useMediaQueryToken } from '@/common/hooks/useMediaQuery'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { DataTable, useDataTableContext } from '@/common/components/data-table'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { sprinkles } from '@/core/styles/sprinkles.css'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { AlertDialog } from '@/common/components/alert-dialog'
import { Badge } from '@/common/components/badge'
import { Input } from '@/common/components/input'
import { Select } from '@/common/components/select'
import {
  useAdminAccountRequestsSource,
  type AdminAccountRequestsSource,
} from '@/features/account-request/data/adminAccountRequestsSource'
import {
  approveAccountRequest,
  rejectAccountRequest,
  type AccountRequestItem,
} from '@/features/account-request/api/adminAccountRequests'
import type { AccountRequestFilters } from '@/features/account-request/data/adminAccountRequestsSource'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: AccountRequestListPage,
})

// Map status to Badge color
const STATUS_BADGE_COLOR: Record<string, 'caution' | 'success' | 'error' | 'neutral'> = {
  pending: 'caution',
  approved: 'success',
  rejected: 'error',
}

// =============================================================================
// URL State Helpers
// =============================================================================

type StatusValue = 'all' | 'pending' | 'approved' | 'rejected'

function parseUrlStatus(value: string | null): StatusValue {
  if (value === 'all' || value === 'pending' || value === 'approved' || value === 'rejected') {
    return value
  }
  return 'pending' // Default to pending
}

type RoleValue = 'all' | 'investor' | 'agent'

function parseUrlRole(value: string | null): RoleValue {
  if (value === 'investor' || value === 'agent') {
    return value
  }
  return 'all' // Default to all roles
}

// =============================================================================
// Display Helpers
// =============================================================================

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function getDisplayName(request: AccountRequestItem): string {
  if (request.first_name && request.last_name) {
    return `${request.first_name} ${request.last_name}`
  }
  if (request.first_name) return request.first_name
  if (request.last_name) return request.last_name
  return '-'
}

// =============================================================================
// Toolbar Component
// =============================================================================

interface ToolbarProps {
  filters: AccountRequestFilters
  onFiltersChange: (filters: AccountRequestFilters) => void
  search: string
  onSearchChange: (value: string) => void
}

function AccountRequestsToolbar({ filters, onFiltersChange, search, onSearchChange }: ToolbarProps) {
  const { view } = useDataTableContext<AdminAccountRequestsSource>()

  // Derive status value for select (empty string = all)
  const statusValue = filters.status || ''

  const handleStatusChange = (value: string) => {
    const newStatus = value === '' ? undefined : (value as 'pending' | 'approved' | 'rejected')
    const newFilters = { ...filters, status: newStatus }
    onFiltersChange(newFilters)
    view.setFilters(newFilters)
  }

  // Derive role value for select (empty string = all)
  const roleValue = filters.requested_role || ''

  const handleRoleChange = (value: string) => {
    const newRole = value === '' ? undefined : (value as 'investor' | 'agent')
    const newFilters = { ...filters, requested_role: newRole }
    onFiltersChange(newFilters)
    view.setFilters(newFilters)
  }

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarSelect}>
        <Select
          label="Status"
          value={statusValue}
          onValueChange={handleStatusChange}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
          ]}
          size="sm"
          optional
          showOptional={false}
          placeholder="All Statuses"
          clearLabel="All Statuses"
        />
      </div>
      <div className={styles.toolbarSelect}>
        <Select
          label="Role"
          value={roleValue}
          onValueChange={handleRoleChange}
          options={[
            { value: 'investor', label: 'Investor' },
            { value: 'agent', label: 'Agent' },
          ]}
          size="sm"
          optional
          showOptional={false}
          placeholder="All Roles"
          clearLabel="All Roles"
        />
      </div>
      <div className={styles.toolbarSearch}>
        <DataTable.Search
          placeholder="Search by email, name, or company..."
          value={search}
          onValueChange={onSearchChange}
        />
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

function AccountRequestListPage() {
  const { roles, token } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const source = useAdminAccountRequestsSource()
  const search = useSearch({ strict: false }) as { status?: string; search?: string; requested_role?: string }

  // Parse URL state
  const urlStatus = parseUrlStatus(search.status ?? null)
  const urlRole = parseUrlRole(search.requested_role ?? null)
  const urlSearch = search.search ?? ''

  // Convert URL state to filters
  const filters = useMemo<AccountRequestFilters>(
    () => ({
      status: urlStatus === 'all' ? undefined : urlStatus,
      requested_role: urlRole === 'all' ? undefined : urlRole,
    }),
    [urlStatus, urlRole]
  )

  // Update URL params helper
  const updateUrl = useCallback(
    (updates: { status?: StatusValue; role?: RoleValue; search?: string }) => {
      navigate({
        search: ((prev: Record<string, string | undefined>) => {
          const next = { ...prev }
          if (updates.status !== undefined) {
            if (updates.status === 'pending') {
              delete next.status // pending is default
            } else {
              next.status = updates.status
            }
          }
          if (updates.role !== undefined) {
            if (updates.role === 'all') {
              delete next.requested_role
            } else {
              next.requested_role = updates.role
            }
          }
          if (updates.search !== undefined) {
            if (updates.search === '') {
              delete next.search
            } else {
              next.search = updates.search
            }
          }
          return next
        }) as never,
        replace: true,
      })
    },
    [navigate]
  )

  // Handle filter change from toolbar
  const handleFiltersChange = useCallback(
    (newFilters: AccountRequestFilters) => {
      const newStatus: StatusValue = newFilters.status || 'all'
      const newRole: RoleValue = newFilters.requested_role || 'all'
      updateUrl({ status: newStatus, role: newRole })
    },
    [updateUrl]
  )

  // Handle search change from toolbar
  const handleSearchChange = useCallback(
    (value: string) => {
      updateUrl({ search: value })
    },
    [updateUrl]
  )

  // Approve confirmation state
  const [approveTarget, setApproveTarget] = useState<AccountRequestItem | null>(null)
  const [isApproving, setIsApproving] = useState(false)

  // Reject confirmation state
  const [rejectTarget, setRejectTarget] = useState<AccountRequestItem | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [isRejecting, setIsRejecting] = useState(false)

  // Check if current user is admin
  const isAdmin = roles.includes('admin')

  // Check if mobile (hide inline actions on mobile, users can click row to view detail page)
  const isMobile = useMediaQueryToken('largePhoneDown')

  // Handle approve click
  const handleApproveClick = useCallback((request: AccountRequestItem) => {
    setApproveTarget(request)
  }, [])

  // Handle confirmed approve
  const handleConfirmApprove = useCallback(async () => {
    if (!token || !approveTarget) return

    setIsApproving(true)
    try {
      await approveAccountRequest(token, approveTarget.id)
      showSuccessToast(`Account request for "${approveTarget.email}" has been approved.`)
      setApproveTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['dataSource', 'admin-account-requests'] })
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to approve account request')
    } finally {
      setIsApproving(false)
    }
  }, [token, approveTarget, showSuccessToast, showErrorToast, queryClient])

  // Handle reject click
  const handleRejectClick = useCallback((request: AccountRequestItem) => {
    setRejectTarget(request)
    setRejectReason('')
  }, [])

  // Handle confirmed reject
  const handleConfirmReject = useCallback(async () => {
    if (!token || !rejectTarget) return

    setIsRejecting(true)
    try {
      await rejectAccountRequest(token, rejectTarget.id, rejectReason || undefined)
      showSuccessToast(`Account request for "${rejectTarget.email}" has been rejected.`)
      setRejectTarget(null)
      setRejectReason('')
      await queryClient.invalidateQueries({ queryKey: ['dataSource', 'admin-account-requests'] })
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to reject account request')
    } finally {
      setIsRejecting(false)
    }
  }, [token, rejectTarget, rejectReason, showSuccessToast, showErrorToast, queryClient])

  // Handle view details
  const handleViewDetails = useCallback(
    (request: AccountRequestItem) => {
      navigate({ to: '/admin/account-requests/$id', params: { id: request.id } })
    },
    [navigate]
  )

  // Non-admin unauthorized view
  if (!isAdmin) {
    return (
      <Stack gap="24" style={{ padding: '24px', height: '100%' }}>
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            Account Requests
          </Text>
          <Text
            variant="bodySm"
            color="muted"
            className={sprinkles({ display: { base: 'block', tabletDown: 'none' } })}
          >
            Review and approve account requests
          </Text>
        </Stack>
        <Stack gap="16" align="center" justify="center" style={{ flex: 1 }}>
          <Icon name="lock" size={48} style={{ color: '#999' }} />
          <Text as="h2" variant="bodyLg" weight="bold">
            Access Restricted
          </Text>
          <Text variant="body" color="muted" style={{ textAlign: 'center', maxWidth: 400 }}>
            Account request management is only available to administrators.
          </Text>
        </Stack>
      </Stack>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Page Header */}
        <div className={styles.header}>
          <Stack gap="4">
            <Text as="h1" variant="h4" weight="bold">
              Account Requests
            </Text>
            <Text
              variant="bodySm"
              color="muted"
              className={sprinkles({ display: { base: 'block', tabletDown: 'none' } })}
            >
              Review and approve account requests
            </Text>
          </Stack>
        </div>

        {/* DataTable */}
        <div className={styles.tableContainer}>
        <DataTable
          source={source}
          getRowKey={(r) => r.id}
          aria-label="Account requests list"
          getRowLabel={(r) => `${r.first_name} ${r.last_name}, ${r.email}`}
          initialFilters={filters as Record<string, unknown>}
          initialSearch={urlSearch}
          emptyMessage="No account requests found"
          hoverable
          stickyHeader
          pinnedLast={!isMobile}
          onRowClick={handleViewDetails}
        >
          <DataTable.Toolbar>
            <AccountRequestsToolbar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              search={urlSearch}
              onSearchChange={handleSearchChange}
            />
          </DataTable.Toolbar>

          <DataTable.Columns>
            {/* Status */}
            <DataTable.Column id="status" header="Status" field="status" width={100}>
              {(request: AccountRequestItem) => (
                <Badge
                  color={STATUS_BADGE_COLOR[request.status] ?? 'neutral'}
                  style={{ textTransform: 'capitalize' }}
                >
                  {request.status}
                </Badge>
              )}
            </DataTable.Column>

            {/* Email & Name */}
            <DataTable.Column id="email" header="Requester" field="email" width={280}>
              {(request: AccountRequestItem) => (
                <Stack gap="4">
                  <Text weight="medium" variant="bodySm">
                    {request.email}
                  </Text>
                  <Text variant="bodyXs" color="muted">
                    {getDisplayName(request)}
                  </Text>
                </Stack>
              )}
            </DataTable.Column>

            {/* Role */}
            <DataTable.Column id="requested_role" header="Role" field="requested_role" width={100}>
              {(request: AccountRequestItem) => (
                <Text variant="bodySm" style={{ textTransform: 'capitalize' }}>
                  {request.requested_role}
                </Text>
              )}
            </DataTable.Column>

            {/* Company / Brokerage */}
            <DataTable.Column id="company" header="Company" field="company_name" width={180}>
              {(request: AccountRequestItem) => (
                <Text variant="bodySm" color={request.company_name ? 'default' : 'muted'}>
                  {request.company_name || '-'}
                </Text>
              )}
            </DataTable.Column>

            {/* Markets */}
            <DataTable.Column id="markets" header="Markets" field="markets" width={150}>
              {(request: AccountRequestItem) => (
                <Text variant="bodySm" color="muted">
                  {request.markets.length > 0 ? request.markets.join(', ') : '-'}
                </Text>
              )}
            </DataTable.Column>

            {/* Submitted */}
            <DataTable.Column id="created_at" header="Submitted" field="created_at" width={120}>
              {(request: AccountRequestItem) => (
                <Text variant="bodySm" color="muted">
                  {formatDateTime(request.created_at)}
                </Text>
              )}
            </DataTable.Column>

            {/* Actions - only show for pending requests, hidden on mobile */}
            {!isMobile && (
              <DataTable.Column id="actions" header="" field={false} width={120} align="right">
                {(request: AccountRequestItem) => {
                  if (request.status !== 'pending') {
                    return null
                  }
                  return (
                    <Stack direction="row" gap="8" justify="end">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRejectClick(request)
                        }}
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleApproveClick(request)
                        }}
                      >
                        Approve
                      </Button>
                    </Stack>
                  )
                }}
              </DataTable.Column>
            )}
          </DataTable.Columns>
        </DataTable>
      </div>

      {/* Approve confirmation dialog */}
      <AlertDialog
        open={!!approveTarget}
        onOpenChange={(open) => !open && setApproveTarget(null)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Approve account request?</AlertDialog.Title>
          <AlertDialog.Description>
            This will create an investor account for "{approveTarget?.email}" and send them a welcome
            email with instructions to set up their password, unless they already have an Orchard
            login.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isApproving}>Cancel</AlertDialog.Cancel>
            <Button variant="primary" onClick={handleConfirmApprove} disabled={isApproving}>
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>

      {/* Reject confirmation dialog */}
      <AlertDialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRejectTarget(null)
            setRejectReason('')
          }
        }}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Reject account request?</AlertDialog.Title>
          <AlertDialog.Description>
            <Stack gap="16">
              <Text>
                This will reject the account request from "{rejectTarget?.email}". They will not be
                notified automatically.
              </Text>
              <Input
                label="Rejection reason (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Incomplete information, not a qualified investor"
              />
            </Stack>
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isRejecting}>Cancel</AlertDialog.Cancel>
            <Button variant="danger" onClick={handleConfirmReject} disabled={isRejecting}>
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
      </div>
    </div>
  )
}
